import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { TextInput } from "../../components/TextInput/TextInput";
import { TextInputWithIcon } from "../../components/TextInputWithIcon/TextInputWithIcon";
import { DateInput } from "../../components/DateInput/DateInput";
import { RadioButtonInput } from "../../components/RadioButtonInput/RadioButtonInput";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { TextAreaInput } from "../../components/TextAreaInput/TextAreaInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { NewPartnerForm } from "../../forms/NewPartnerForm/NewPartnerForm";
import { DocumentOfReceive } from "../../models/DocumentOfReceive";
import { Product } from "../../models/Product";
import { Warehouse } from "../../models/Warehouse";
import { Partner } from "../../models/Partner";
import { RoleType } from "../../models/Role";
import { TransactionType } from "../../models/Transaction";
import { MercantileType } from "../../models/ParamSettings";
import { getMercantileTemplate } from "../../document-templates/MercantileTemplate";
import { activeUser } from "../../services/AuthService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllPartners } from "../../services/PartnerService";
import { getAllSettings } from "../../services/SettingsService";
import { getOneDocument } from "../../services/NewVersionService";
import "./NewDocumentOfReceivePage.scss";

export interface DocumentOfReceivePageProps {
  show?: string;
  buttonSaveStyle?: React.CSSProperties;
  buttonNewPartnerStyle?: React.CSSProperties;
  buttonResetStyle?: React.CSSProperties;
}

export const NewDocumentOfReceivePreviewPage = (
  p: DocumentOfReceivePageProps,
) => {
  const { show } = p;

  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [newDocumentOfReceive, setNewDocumentOfReceive] =
    useState<DocumentOfReceive>({
      id: id ?? "",
      warehouse:
        activeUser?.role?.name === RoleType.Warehouseman
          ? (cloneDeep(activeUser.warehouse) as Warehouse)
          : ({} as Warehouse),
      warehouseman: `${activeUser?.firstName} ${activeUser?.lastName}`,
      type: TransactionType.Import,
      date: new Date(),
      time: new Date(),
      brutto: undefined,
      tara: undefined,
      netto: undefined,
      srps: undefined,
      product: {} as Product,
      productWithCode: "",
      partner: {} as Partner,
      shipper: {} as Partner,
      driverName: "",
      vehicleNumber: "",
      moisture: undefined,
      impurity: undefined,
      breakage: undefined,
      defect: undefined,
      hectoliter: undefined,
      protein: undefined,
      gluten: undefined,
      cellNumberFloor: undefined,
      hybrid: "",
      dryingCosts: undefined,
      dryWeight: undefined,
      shippingSlip: "",
      calculationMethod: true,
      foreignProduct: false,
      productOwner: {} as Partner,
      note: "",
      finishedDocument: false,
      finishedDocumentFirstTime: false,
    });

  const [processing, setProcessing] = useState<boolean>(false);
  const [isPdfReady, setIsPdfReady] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [pdfData, setPdfData] = useState<any>(
    getMercantileTemplate(undefined, true, newDocumentOfReceive, []),
  );

  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [partnersApi, setPartnersApi] = useState<Partner[]>([]);
  const [settingsApi, setSettingsApi] = useState<any[]>([]);

  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);
  const toast = useRef<any>(null);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshLookupsAndSettings = async () => {
    const [wResp, partnerResp, sResp] = await Promise.all([
      getAllWarehouses(),
      getAllPartners(),
      getAllSettings(),
    ]);

    [wResp, partnerResp, sResp].forEach(ensureSuccess);

    setWarehousesApi(wResp?.data?.result ?? []);
    setPartnersApi(partnerResp?.data?.result ?? []);
    setSettingsApi(sResp?.data?.result ?? []);
  };

  useEffect(() => {
    setProcessing(true);
    refreshLookupsAndSettings()
      .catch((e: any) => {
        toast.current?.show({
          severity: "error",
          detail: e?.message ?? String(t("Greška pri učitavanju podataka")),
          life: 3500,
        });
      })
      .finally(() => setProcessing(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (id) {
      setProcessing(true);
      setIsPdfReady(false);
      getOneDocument(id)
        .then((resp: any) => {
          ensureSuccess(resp);
          const document = resp?.data?.result;
          if (!document) return;

          if (document.date) document.date = new Date(document.date);
          if (document.time) document.time = new Date(document.time);
          if (!document.warehouseman) {
            document.warehouseman = `${activeUser?.firstName} ${activeUser?.lastName}`;
          }
          setNewDocumentOfReceive(document);
          setIsPdfReady(true);
        })
        .catch((e: any) => {
          toast.current?.show({
            severity: "error",
            detail: e?.message ?? String(t("Greška pri učitavanju dokumenta")),
            life: 3500,
          });
        })
        .finally(() => setProcessing(false));
    }
  }, [id]);

  // populate pdf when data changes
  useEffect(() => {
    let categoryType: MercantileType = MercantileType.Wheat;
    if (newDocumentOfReceive.product?.category?.name === "Uljana repica")
      categoryType = MercantileType.OiledBeet;
    if (newDocumentOfReceive.product?.category?.name === "Ječam")
      categoryType = MercantileType.Barley;
    if (newDocumentOfReceive.product?.category?.name === "Soja")
      categoryType = MercantileType.Soya;
    if (newDocumentOfReceive.product?.category?.name === "Suncokret")
      categoryType = MercantileType.Sunflower;
    if (newDocumentOfReceive.product?.category?.name === "Kukuruz")
      categoryType = MercantileType.Corn;
    if (
      isPdfReady &&
      settingsApi.length > 0 &&
      Object.keys(newDocumentOfReceive).length > 0
    ) {
      const timer = setTimeout(() => {
        const newPdf = getMercantileTemplate(
          categoryType,
          true,
          newDocumentOfReceive,
          settingsApi,
        );
        setPdfData(newPdf);
        setShowPdf(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [newDocumentOfReceive, isPdfReady, settingsApi]);

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "all",
  });

  const handleChange = (prop: string, value: any) => {};

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="form-wrapper">
        <div className="form-header">
          <div className="header-title">
            <i
              className="bi bi-truck header-title-icon"
              style={{ fontSize: 18 }}
            ></i>
            <span className="header-title">
              {`${t("Pregled prijemnice broj")}:`}{" "}
              {newDocumentOfReceive?.finishedDocumentNumber ??
                newDocumentOfReceive.finishedDocumentId ??
                ""}
            </span>
          </div>
          <div className="header-buttons">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={t("Sve prijemnice")}
              icon="bi bi-arrow-left-square"
              onButtonClick={() => {
                if (newDocumentOfReceive?.isNatural) {
                  navigate(`/processing-product-inputs`);
                } else {
                  navigate(`/mercantile-input-fast`);
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (
                  newDocumentOfReceive.finishedDocument === true &&
                  newDocumentOfReceive.finishedDocumentId
                ) {
                  if (downloadPdfDocument.current) {
                    // @ts-ignore
                    downloadPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće preuzeti prijemnicu koja nije zaključena i snimljena.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (
                  newDocumentOfReceive.finishedDocument === true &&
                  newDocumentOfReceive.finishedDocumentId
                ) {
                  if (printPdfDocument.current) {
                    // @ts-ignore
                    printPdfDocument.current();
                  }
                } else {
                  //@ts-ignore
                  toast.current.show({
                    severity: "error",
                    detail: t(
                      `Nije moguće odštampati prijemnicu koja nije zaključena i snimljena.`,
                    ),
                    life: 3000,
                  });
                }
              }}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="form-body">
            <div className="form-input-fields">
              {activeUser?.role?.name === RoleType.Warehouseman ? (
                <TextInput
                  id="warehouse"
                  value={activeUser?.warehouse?.name ?? ""}
                  onValueChange={handleChange}
                  labelText={t("Magacin")}
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={true}
                />
              ) : (
                <DropdownInput
                  id="warehouse"
                  value={newDocumentOfReceive.warehouse.id}
                  onValueChange={handleChange}
                  optionValue="id"
                  optionLabel="name"
                  options={warehousesApi}
                  filter={false}
                  labelText={t("Magacin")}
                  placeholderText={t("Izaberite iz liste")}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={true}
                />
              )}
              <DateInput
                id="date"
                value={
                  newDocumentOfReceive?.finishedDocumentFirstTime
                    ? (newDocumentOfReceive.date as Date)
                    : new Date()
                }
                onValueChange={handleChange}
                labelText={t("Datum")}
                placeholderText={t("Izaberite datum")}
                icon="bi bi-calendar-check"
                maxDate={new Date()}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInput
                id="warehouseman"
                value={newDocumentOfReceive.warehouseman as string}
                onValueChange={handleChange}
                labelText={t("Magacioner")}
                placeholderText={t("Unesite ime magacionera")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <DateInput
                id="time"
                value={
                  newDocumentOfReceive?.finishedDocumentFirstTime
                    ? (newDocumentOfReceive.time as Date)
                    : new Date()
                }
                onValueChange={handleChange}
                labelText={t("Vreme")}
                placeholderText={t("Izaberite vreme")}
                timeOnly={true}
                icon="bi bi-clock"
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <DropdownInput
                id="partner"
                value={newDocumentOfReceive.partner?.id as string}
                optionValue="id"
                optionLabel="name"
                onValueChange={handleChange}
                options={partnersApi}
                filter={true}
                labelText={t("Dobavljač")}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <div className="new-partner-button">
                {(activeUser?.role?.name === RoleType.Superadmin ||
                  activeUser?.role?.name === RoleType.Administrator ||
                  activeUser?.role?.name === RoleType.Warehouseman) && (
                  <NewPartnerForm
                    buttonLabel={`${t("Dodaj dobavljača")}`}
                    buttonIcon="bi bi-plus-square"
                    show={show}
                    buttonStyle={{
                      border: "none",
                      background: "rgba(21, 115, 71, 1)",
                      color: "#ffffff",
                      marginRight: "1rem",
                      margin: "0 1rem 0 0",
                    }}
                    buttonNewCityStyle={{
                      border: "none",
                      background: "rgba(21, 115, 71, 1)",
                    }}
                    buttonSaveStyle={{
                      border: "none",
                      background: "rgba(0, 165, 83, 1)",
                    }}
                    buttonInactiveStyle={{
                      border: "none",
                      background: "rgba(108, 117, 125, 1)",
                    }}
                  />
                )}
              </div>
              <DropdownInput
                id="shipper"
                value={newDocumentOfReceive.shipper?.id as string}
                optionValue="id"
                optionLabel="name"
                onValueChange={handleChange}
                options={partnersApi}
                filter={true}
                labelText={t("Prevoznik")}
                placeholderText={t("Izaberite iz liste")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInput
                id="driverName"
                value={newDocumentOfReceive.driverName as string}
                onValueChange={handleChange}
                labelText={t("Ime vozača")}
                placeholderText={t("Unesite ime vozača")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInput
                id="vehicleNumber"
                value={(
                  newDocumentOfReceive.vehicleNumber as string
                )?.toUpperCase()}
                onValueChange={handleChange}
                labelText={t("Registarski broj vozila")}
                placeholderText={t("Unesite registarski broj")}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInputWithIcon
                id="shippingSlip"
                value={newDocumentOfReceive.shippingSlip as string}
                onValueChange={handleChange}
                labelText={t("Otpremni listić")}
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInputWithIcon
                id="sentQuantity"
                value={(newDocumentOfReceive.sentQuantity as number) ?? 0}
                onValueChange={handleChange}
                labelText={t("Upućena količina")}
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInputWithIcon
                id="cellNumberFloor"
                value={newDocumentOfReceive.cellNumberFloor as string}
                onValueChange={handleChange}
                labelText={t("Broj ćelije / podni magacin")}
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInput
                id="productCategory"
                value={newDocumentOfReceive.product?.category?.name as string}
                onValueChange={handleChange}
                labelText={t("Vrsta robe")}
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInput
                id="product"
                value={newDocumentOfReceive.product?.name as string}
                onValueChange={handleChange}
                labelText={t("Naziv robe")}
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInputWithIcon
                id="brutto"
                value={(newDocumentOfReceive.brutto as number) ?? ""}
                onValueChange={handleChange}
                labelText={t("Bruto")}
                icon="bi bi-minecart-loaded"
                iconAlign="right"
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInputWithIcon
                id="tara"
                value={(newDocumentOfReceive.tara as number) ?? ""}
                onValueChange={handleChange}
                labelText={t("Tara")}
                icon="bi bi-minecart"
                iconAlign="right"
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInputWithIcon
                id="moisture"
                value={(newDocumentOfReceive.moisture as number) ?? ""}
                onValueChange={handleChange}
                labelText={t("Vlaga")}
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              <TextInputWithIcon
                id="impurity"
                value={newDocumentOfReceive.impurity as number}
                onValueChange={handleChange}
                labelText={t("Primese")}
                icon="bi bi-clipboard"
                iconAlign="right"
                placeholderText=""
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={true}
              />
              {newDocumentOfReceive?.product?.category?.name === "Kukuruz" && (
                <TextInputWithIcon
                  id="breakage"
                  value={(newDocumentOfReceive.breakage as number) ?? ""}
                  onValueChange={handleChange}
                  labelText={t("Lom")}
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={true}
                />
              )}
              {newDocumentOfReceive?.product?.category?.name === "Kukuruz" && (
                <TextInputWithIcon
                  id="defect"
                  value={(newDocumentOfReceive.defect as number) ?? ""}
                  onValueChange={handleChange}
                  labelText={t("Defekt")}
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={true}
                />
              )}
              {(newDocumentOfReceive?.product?.category?.name === "Pšenica" ||
                newDocumentOfReceive?.product?.category?.name === "Ječam") && (
                <TextInputWithIcon
                  id="hectoliter"
                  value={(newDocumentOfReceive.hectoliter as number) ?? ""}
                  onValueChange={handleChange}
                  labelText={t("Hektolitar")}
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={true}
                />
              )}
              {newDocumentOfReceive?.product?.category?.name === "Pšenica" && (
                <TextInputWithIcon
                  id="protein"
                  value={(newDocumentOfReceive.protein as number) ?? ""}
                  onValueChange={handleChange}
                  labelText={t("Protein")}
                  icon="bi bi-clipboard"
                  iconAlign="right"
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={true}
                />
              )}
              {newDocumentOfReceive?.product?.category?.name === "Ječam" && (
                <div className="empty-field"></div>
              )}
              {newDocumentOfReceive?.isNatural !== true && (
                <RadioButtonInput
                  id="foreignProduct"
                  value={newDocumentOfReceive.foreignProduct}
                  radioButtonValue1={t("DA")}
                  radioButtonValue2={t("NE")}
                  onValueChange={handleChange}
                  labelText={t("Tuđa roba na skladištenju")}
                  control={control}
                  errors={errors}
                  disabled={true}
                />
              )}
              {newDocumentOfReceive?.isNatural !== true && (
                <RadioButtonInput
                  id="calculationMethod"
                  value={newDocumentOfReceive.calculationMethod}
                  radioButtonValue1={t("Svodjenje na SRPS")}
                  radioButtonValue2={t("Neto")}
                  onValueChange={handleChange}
                  labelText={t("Metod obračuna")}
                  control={control}
                  errors={errors}
                  disabled={true}
                />
              )}
              <div className="note-field">
                <TextAreaInput
                  id="note"
                  value={newDocumentOfReceive.note as string}
                  onValueChange={handleChange}
                  rows={1}
                  cols={50}
                  labelText={t("Napomena")}
                  placeholderText=""
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  disabled={true}
                />
              </div>
              <RadioButtonInput
                id="finishedDocument"
                value={newDocumentOfReceive.finishedDocument}
                radioButtonValue1={t("DA")}
                radioButtonValue2={t("NE")}
                onValueChange={handleChange}
                labelText={t("Zaključena prijemnica")}
                control={control}
                errors={errors}
                disabled={true}
              />
            </div>
            <div className="documents-preview">
              {!showPdf || !pdfData ? (
                <p>Učitavam PDF prikaz...</p>
              ) : (
                <PdfPreview
                  pdfDefinition={pdfData}
                  downloadDocument={downloadPdfDocument}
                  printDocument={printPdfDocument}
                  documentName={`Prijemnica_${newDocumentOfReceive.finishedDocumentId ?? ""}`}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
